import { useCallback } from 'react';
import { get } from 'lodash-es';
import {
  useLocales,
  useSetEntityData,
  useEntityData,
} from '@web-config-app/core-react';

/**
 * Props for the useLocalizedArrayOperations hook
 * This hook handles array operations across non-default locales
 * Creates localized arrays matching the default array length if they don't exist
 * Fills missing elements with empty strings as placeholders
 */
export interface UseLocalizedArrayOperationsProps {
  /**
   * The path to the array property in the entity data - will be present in non-page level arrays
   */
  path?: string;
  /**
   * The form path (prefix for the path)
   */
  formPath?: string;
  /**
   * Placeholder value to use when replacing null values
   */
  placeholderValue: '' | {};
}

interface LocalizedArrayOperationProps {
  /**
   * The index of the item to move up or down
   */
  index: number;
}

export const useLocalizedArrayOperations = ({
  path,
  formPath,
  placeholderValue,
}: UseLocalizedArrayOperationsProps) => {
  // formPath is required for this hook to function correctly
  if (!formPath) {
    throw new Error('formPath is required for useLocalizedArrayOperations');
  }
  const { locales = [] } = useLocales();
  const setEntityData = useSetEntityData();
  const entityData = useEntityData();

  // Remove the default locale from the locales array as it's handled by the array control directly
  const nonDefaultLocales = locales.filter((locale) => !locale.isDefault);

  const fullPath = path ? `${formPath}.${path}` : formPath;
  const localizedPath = `domainEntityAttributes.${fullPath}`;
  /**
   * Helper function to perform the same array operation across all existing non-default locales.
   *
   * Important: This ensures all locales match the current (possibly updated) default array length
   * by checking the actual entity data rather than relying on the defaultArrayLength prop
   *  - Only performs operations for locales that exist under translations data already.
   *  - Only performs operations if the target property already exists in the translations locale data AND is an array.
   *  - If the localized array exists but is shorter than the default locale array, it is padded
   *    with the placeholder value so indices align for the operation
   */
  const performOperationAcrossLocales = useCallback(
    (operation: (data: any[]) => any[]) => {
      // Get the actual current length from the default locale's data - this may have been
      // updated since our component mounted, especially when adding items
      const defaultLocaleData =
        entityData?.attributes?.domainEntityAttributes || {};

      const defaultLocaleArray = get(defaultLocaleData, fullPath);

      nonDefaultLocales.forEach((locale) => {
        const localeData =
          entityData?.attributes?.translations?.[locale.code]
            ?.domainEntityAttributes || {};

        // don't add arrays for locales that don't exist yet.
        if (!localeData) return;

        let localeArrayData = get(localeData, fullPath);

        // only proceed if property exists in nonDefaultLocale AND is an array, we don't create new arrays here
        if (!Array.isArray(localeArrayData)) return;

        // If we have a valid default locale array, and localized array is shorter, pad with placeholder values
        if (
          Array.isArray(defaultLocaleArray) &&
          localeArrayData.length < defaultLocaleArray.length
        ) {
          const paddingNeeded =
            defaultLocaleArray.length - localeArrayData.length;
          localeArrayData = [
            ...localeArrayData,
            ...Array(paddingNeeded).fill(placeholderValue),
          ];
        }

        // Sanitize input data to replace null values with empty strings
        const sanitizedInputData = localeArrayData.map((item: any) =>
          item === null ? placeholderValue : item,
        );

        // Perform the operation on the sanitized data
        const updatedLocaleData = operation([...sanitizedInputData]);

        // Ensure updatedLocaleData is an array before proceeding
        if (Array.isArray(updatedLocaleData)) {
          // Sanitize the result data to ensure no null values
          const sanitizedResultData = updatedLocaleData.map((item: any) =>
            item === null ? placeholderValue : item,
          );

          // Only update if the operation actually changed something
          if (
            JSON.stringify(localeArrayData) !==
            JSON.stringify(sanitizedResultData)
          ) {
            // Always pass the sanitized array to setEntityData
            setEntityData(sanitizedResultData, localizedPath, locale.code);
          }
        }
      });
    },
    [
      entityData?.attributes?.translations,
      entityData?.attributes?.domainEntityAttributes,
      fullPath,
      nonDefaultLocales,
      placeholderValue,
      setEntityData,
      localizedPath,
    ],
  );

  /**
   * Move an item up in the localized array
   * Creates arrays for locales that don't have data yet, matching the default locale's length
   * Fills new arrays with empty strings
   *
   * Note: This implementation ensures non-default locales stay in sync with the default locale
   * array order, even when the default locale has been updated through moveUp actions
   */
  const handleLocalizedMoveUp = useCallback(
    ({ index }: LocalizedArrayOperationProps) => {
      performOperationAcrossLocales((data) => {
        const newData = [...data];
        // Ensure we don't operate on null values
        if (index > 0 && index < newData.length) {
          // If either value is null, replace with empty string
          if (newData[index - 1] === null)
            newData[index - 1] = placeholderValue;
          if (newData[index] === null) newData[index] = placeholderValue;

          // Swap positions
          [newData[index - 1], newData[index]] = [
            newData[index],
            newData[index - 1],
          ];
        }
        return newData;
      });
    },
    [performOperationAcrossLocales, placeholderValue],
  );

  /**
   * Move an item down in the localized array
   * Creates arrays for locales that don't have data yet, matching the default locale's length
   * Fills new arrays with empty strings
   *
   * Note: This implementation ensures non-default locales stay in sync with the default locale
   * array order, even when the default locale has been updated through moveDown actions
   */
  const handleLocalizedMoveDown = useCallback(
    ({ index }: LocalizedArrayOperationProps) => {
      performOperationAcrossLocales((data) => {
        const newData = [...data];
        // Ensure we don't operate on null values and stay within array bounds
        if (index >= 0 && index < newData.length - 1) {
          // If either value is null, replace with placeholderValue
          if (newData[index] === null) newData[index] = placeholderValue;
          if (newData[index + 1] === null)
            newData[index + 1] = placeholderValue;

          // Swap positions
          [newData[index], newData[index + 1]] = [
            newData[index + 1],
            newData[index],
          ];
        }
        return newData;
      });
    },
    [performOperationAcrossLocales, placeholderValue],
  );

  /**
   * Remove an item from the localized array
   * Creates arrays for locales that don't have data yet, matching the default locale's length
   * Fills new arrays with empty strings
   *
   * Note: This implementation ensures non-default locales stay in sync with the default locale
   * array length, even when the default locale has been updated through removeItems actions
   */
  const handleLocalizedRemoveItem = useCallback(
    ({ index }: LocalizedArrayOperationProps) => {
      performOperationAcrossLocales((data) => {
        // First ensure we have no null values in the array
        const sanitizedData = data.map((item) =>
          item === null ? placeholderValue : item,
        );
        const newData = [...sanitizedData];

        // Only attempt to remove if the index is valid
        if (index >= 0 && index < newData.length) {
          newData.splice(index, 1);
        }

        return newData;
      });
    },
    [performOperationAcrossLocales, placeholderValue],
  );

  /**
   * Add a placeholder item to the localized array
   * Creates arrays for locales that don't have data yet, matching the default locale's length
   * Adds an empty string as a placeholder for the new item
   *
   * Note: This implementation ensures non-default locales stay in sync with the default locale
   * array length, even when the default locale has been updated through addItem actions
   */
  const addLocalizedArrayItem = useCallback(() => {
    performOperationAcrossLocales((data) => {
      const newData = [...data];
      newData.push(placeholderValue);
      return newData;
    });
  }, [performOperationAcrossLocales, placeholderValue]);

  /**
   * Duplicate an item in the localized array
   * For each non-default locale, duplicates the translated item at the specified index
   * and appends it to the end of the array
   *
   * @param index - The index of the item to duplicate
   */
  const handleLocalizedDuplicateItem = useCallback(
    ({ index }: LocalizedArrayOperationProps) => {
      performOperationAcrossLocales((data) => {
        const newData = [...data];

        // Only attempt to duplicate if the index is valid
        if (index >= 0 && index < newData.length) {
          // Get the item to duplicate
          const itemToDuplicate =
            newData[index] === null ? placeholderValue : newData[index];

          // Add the duplicate item to the end of the array
          newData.push(itemToDuplicate);
        }

        return newData;
      });
    },
    [performOperationAcrossLocales, placeholderValue],
  );

  /**
   * Move an item to a specific position in the localized array
   * For each non-default locale, moves the translated item from the current index to the target index
   *
   * @param fromIndex - The current index of the item to move
   * @param toIndex - The target index where the item should be moved
   */
  const handleLocalizedMoveToPosition = useCallback(
    ({ fromIndex, toIndex }: { fromIndex: number; toIndex: number }) => {
      performOperationAcrossLocales((data) => {
        const newData = [...data];

        // Only attempt to move if both indices are valid
        if (
          fromIndex >= 0 &&
          fromIndex < newData.length &&
          toIndex >= 0 &&
          toIndex < newData.length &&
          fromIndex !== toIndex
        ) {
          // Ensure we don't operate on null values
          if (newData[fromIndex] === null)
            newData[fromIndex] = placeholderValue;

          // Remove the item from its current position
          const [movedItem] = newData.splice(fromIndex, 1);

          // Insert the item at the target position
          newData.splice(toIndex, 0, movedItem);
        }

        return newData;
      });
    },
    [performOperationAcrossLocales, placeholderValue],
  );

  return {
    handleLocalizedMoveUp,
    handleLocalizedMoveDown,
    handleLocalizedRemoveItem,
    addLocalizedArrayItem,
    handleLocalizedDuplicateItem,
    handleLocalizedMoveToPosition,
  };
};
